/**
 * Rain audio manager.
 * Crossfades between rain-soft.mp3 and rain-strong.mp3 based on rain intensity.
 *
 * Intensity zones:
 *   0 → CROSSFADE_LOW   : only rain-soft, volume ramps up
 *   CROSSFADE_LOW → HIGH : crossfade — soft fades out, strong fades in
 *   CROSSFADE_HIGH → 1   : only rain-strong, full volume
 *
 * Both tracks are always faded in/out smoothly, never cut abruptly.
 */

import { Howl, Howler } from 'howler'
import { assetUrl } from '../../utils/pathResolver'
import { unlockAudioContext } from './unlock-audio-context'

const SFX_BASE = '/assets/audio/sfx'
const CROSSFADE_LOW = 0.45
const CROSSFADE_HIGH = 0.65
const FADE_MS = 800

export interface RainSfxConfig {
  soft: string
  strong: string
}

export class RainAudioManager {
  private soft: Howl
  private strong: Howl
  private softUrl: string
  private strongUrl: string
  private softId: number | null = null
  private strongId: number | null = null
  private isPlaying = false
  private volume = 0.7
  private fadeOutTimer: ReturnType<typeof setTimeout> | null = null

  constructor(config: RainSfxConfig) {
    this.softUrl = assetUrl(`${SFX_BASE}/${config.soft}`)
    this.strongUrl = assetUrl(`${SFX_BASE}/${config.strong}`)

    this.soft = new Howl({
      src: [this.softUrl],
      loop: true,
      volume: 0,
      preload: false,
      html5: false,
    })

    this.strong = new Howl({
      src: [this.strongUrl],
      loop: true,
      volume: 0,
      preload: false,
      html5: false,
    })
  }

  private loadLowPriority(howl: Howl, url: string): void {
    if (howl.state() !== 'unloaded') return
    fetch(url, { priority: 'low' } as RequestInit)
      .catch(() => {})
      .finally(() => { if (howl.state() === 'unloaded') howl.load() })
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v))
  }

  /**
   * Called on every scroll update from the rain manager.
   * intensity: 0-1 when raining, null when rain is off.
   */
  updateIntensity(intensity: number | null): void {
    if (intensity === null || intensity <= 0) {
      this.stopAll()
      return
    }

    if (this.fadeOutTimer) {
      clearTimeout(this.fadeOutTimer)
      this.fadeOutTimer = null
    }

    const clamped = Math.max(0, Math.min(1, intensity))

    let softVol: number
    let strongVol: number

    if (clamped <= CROSSFADE_LOW) {
      softVol = clamped / CROSSFADE_LOW
      strongVol = 0
    } else if (clamped <= CROSSFADE_HIGH) {
      const t = (clamped - CROSSFADE_LOW) / (CROSSFADE_HIGH - CROSSFADE_LOW)
      softVol = 1 - t
      strongVol = t
    } else {
      softVol = 0
      strongVol = 1
    }

    softVol *= this.volume
    strongVol *= this.volume

    this.ensurePlaying()

    if (this.softId != null) this.soft.volume(softVol, this.softId)
    if (this.strongId != null) this.strong.volume(strongVol, this.strongId)
  }

  private ensurePlaying(): void {
    if (this.isPlaying) return

    const startTrack = (howl: Howl): number | null => {
      const doPlay = () => {
        if (this.isPlaying) {
          const id = howl.play() as unknown as number
          howl.volume(0, id)
          if (howl === this.soft) this.softId = id
          else this.strongId = id
        }
      }

      howl.once('playerror', () => {
        unlockAudioContext()
        const ctx = Howler.ctx as AudioContext | undefined
        if (ctx && ctx.state === 'suspended') {
          ctx.resume().then(() => { howl.once('unlock', doPlay) })
        } else {
          howl.once('unlock', doPlay)
        }
      })

      if (howl.state() !== 'loaded') {
        howl.once('load', doPlay)
        if (howl.state() === 'unloaded') {
          this.loadLowPriority(howl, howl === this.soft ? this.softUrl : this.strongUrl)
        }
        return null
      }
      const id = howl.play() as unknown as number
      howl.volume(0, id)
      return id
    }

    this.softId = startTrack(this.soft)
    this.strongId = startTrack(this.strong)
    this.isPlaying = true
  }

  private stopAll(): void {
    if (!this.isPlaying) return

    const fadeThenStop = (howl: Howl, id: number | null) => {
      if (id == null) return
      const cur = howl.volume(id) as number
      if (cur > 0) {
        howl.fade(cur, 0, FADE_MS, id)
        return id
      }
      howl.stop(id)
      return null
    }

    const sId = fadeThenStop(this.soft, this.softId)
    const stId = fadeThenStop(this.strong, this.strongId)

    this.fadeOutTimer = setTimeout(() => {
      if (sId != null) this.soft.stop(sId)
      if (stId != null) this.strong.stop(stId)
      this.fadeOutTimer = null
    }, FADE_MS)

    this.softId = null
    this.strongId = null
    this.isPlaying = false
  }

  destroy(): void {
    if (this.fadeOutTimer) {
      clearTimeout(this.fadeOutTimer)
      this.fadeOutTimer = null
    }
    this.soft.stop()
    this.strong.stop()
    this.soft.unload()
    this.strong.unload()
    this.softId = null
    this.strongId = null
    this.isPlaying = false
  }
}
